import React, { useEffect, useState } from "react";
import {
  Book,
  GraduationCap,
  BookOpenText,
  SquareCheck,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../services/axios";

const MATIERES = [
  "Français",
  "Mathématiques",
  "Physique Chimie",
  "Sciences de la vie et de la Terre",
  "Anglais",
  "Espagnol",
  "Allemand",
  "Chinois",
  "Latin",
  "Histoire-géographie",
  "Sciences économiques et sociales",
];

const CLASSES = [
  "6ème",
  "5ème",
  "4ème",
  "3ème",
  "2nde",
  "1ère",
  "Terminale",
  "CP",
  "CE1",
  "CE2",
  "CM1",
  "CM2",
];

export default function BooksPage() {
  type Book = {
    id: string;
    title: string;
    gradeLevel: string;
    subject: string;
    edition: string;
    year: number;
    condition: string;
    imageUrl: string;
    available: boolean;
    price: number;
    additionalInfo?: string;
    publishedAt: string;
    phoneNumber: string;
    user?: {
      email: string;
      phone?: string;
      firstName: string;
      lastName: string;
      id: string;
    };
  };

  const { isAuthenticated, userId } = useAuth();

  const [books, setBooks] = useState<Book[]>([]);
  const [resetTriggered, setResetTriggered] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [available, setAvailable] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const fetchBooks = async () => {
    try {
      const params = new URLSearchParams();

      if (searchTitle) params.append("title", searchTitle);
      if (grade) params.append("gradeLevel", grade);
      if (subject) params.append("subject", subject);
      if (available !== "") params.append("available", available);
      if (onlyMine) params.append("onlyMine", "true");

      const response = await api.get(`/books?${params.toString()}`);
      setBooks(response.data);
    } catch (error) {}
  };

  const handleUpdateAvailability = async (
    bookId: string,
    available: boolean
  ) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      await fetch(`${API_BASE_URL}/books/${bookId}/availability`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({ available, userId }),
      });
      fetchBooks();
    } catch (error) {
      console.error("Erreur lors de la mise à jour");
    }
  };

  useEffect(() => {
    fetchBooks();
  }, [searchTitle, grade, subject, available, onlyMine]);

  useEffect(() => {
    if (resetTriggered) {
      fetchBooks();
      setResetTriggered(false);
    }
  }, [resetTriggered]);

  return (
    <div className="p-4 sm:p-6 md:p-8">
      {/* Filtres */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">
          Rechercher et filtrer les livres
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
          {/* Input Titre */}
          <div className="relative">
            <Book className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Titre du livre"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Dropdown Classe */}
          <div className="relative">
            <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Toutes les classes</option>
              {CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Dropdown Matière */}
          <div className="relative">
            <BookOpenText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Toutes les matières</option>
              {MATIERES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/* Dropdown Disponibilité */}
          <div className="relative">
            <SquareCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            <select
              value={available}
              onChange={(e) => setAvailable(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Tous les états</option>
              <option value="true">Disponible</option>
              <option value="false">Vendu</option>
            </select>
          </div>
        </div>

        {isAuthenticated && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="onlyMine"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <label htmlFor="onlyMine" className="text-sm text-gray-700">
              Voir uniquement mes livres
            </label>
          </div>
        )}

        {isAuthenticated && (
          <div className="mt-4 inline-block max-w-full bg-yellow-50 border border-yellow-200 rounded-md px-4 py-2 text-sm text-yellow-800">
            Une fois le livre vendu, veuillez cliquer sur l’icône verte{" "}
            <strong>Disponible</strong> pour la convertir en icône rouge{" "}
            <strong>Vendu</strong>. L’annonce sera alors automatiquement
            supprimée. Merci.
          </div>
        )}

        {/* Boutons */}
        <div className="flex flex-col sm:flex-row justify-center sm:justify-end gap-3 mt-2">
          <button
            onClick={fetchBooks}
            className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 rounded-l-2xl"
          >
            {searchTitle || grade || available || subject || onlyMine
              ? "Rechercher"
              : "Tout afficher"}
          </button>
          <button
            onClick={() => {
              setSearchTitle("");
              setGrade("");
              setSubject("");
              setAvailable("");
              setOnlyMine(false);
              setResetTriggered(true);
            }}
            className="bg-gray-200 text-gray-700 px-4 py-2 hover:bg-gray-300 rounded-r-2xl"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Affichage des livres */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
        {books.map((book) => (
          <div
            key={book.id}
            className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col transition hover:shadow-lg"
          >
            {/* Image */}
            <div className="w-full h-48 bg-gray-100 overflow-hidden">
              <img
                className="w-full h-full object-cover object-center"
                src={book.imageUrl}
                alt={book.title}
              />
            </div>

            <div className="px-5 pt-4 text-sm text-gray-600 border-b pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-3">
                <div className="flex-shrink-0 bg-gray-200 rounded-full h-12 w-12 flex items-center justify-center text-base font-semibold text-gray-700">
                  {book.user?.firstName?.[0] || "?"}
                </div>

                {/* Infos utilisateur */}
                <div className="flex flex-col text-sm space-y-0.5">
                  <p className="text-base font-semibold text-gray-800">
                    {book.user?.firstName} {book.user?.lastName}
                  </p>

                  <a
                    href={`mailto:${book.user?.email}?subject=Intérêt pour votre livre "${book.title}"&body=Bonjour,%0A%0AJe suis intéressé par votre annonce concernant le livre suivant :%0A%0ATitre : ${book.title}%0AClasse : ${book.gradeLevel}%0AMatière : ${book.subject}%0APrix : ${book.price}€%0A%0ACordialement.`}
                    className="text-xs text-indigo-600 hover:underline break-all"
                  >
                    {book.user?.email}
                  </a>

                  {book?.phoneNumber && (
                    <a
                      href={`tel:${book.phoneNumber}`}
                      className="text-xs text-indigo-600 hover:underline break-all"
                    >
                      {book.phoneNumber}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Détails du livre */}
            <div className="px-5 py-4 flex-1 flex flex-col justify-between">
              <div className=" pb-4">
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <span className="bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-full">
                    {book.price}€
                  </span>
                  <span className="bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-full">
                    {book.condition}
                  </span>
                  <span className="bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-full">
                    {book.edition.length > 15
                      ? `${book.edition.slice(0, 15)}...`
                      : book.edition}
                  </span>
                  {book.additionalInfo && (
                    <span
                      className="bg-yellow-100 text-yellow-800 font-medium px-3 py-1 rounded-full"
                      title={book.additionalInfo}
                    >
                      {book.additionalInfo.length > 50
                        ? `${book.additionalInfo.slice(0, 50)}...`
                        : book.additionalInfo}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-3 text-sm font-semibold">
                {isAuthenticated && userId === book.user?.id ? (
                  <div className="flex items-center gap-3">
                    <input
                      id={`available-${book.id}`}
                      type="checkbox"
                      checked={book.available}
                      onChange={(e) =>
                        handleUpdateAvailability(book.id, e.target.checked)
                      }
                      className={`h-4 w-4 rounded border-gray-300 focus:ring-0 ${
                        book.available ? "text-green-600" : "text-red-600"
                      }`}
                    />
                    <label
                      htmlFor={`available-${book.id}`}
                      className={
                        book.available ? "text-green-600" : "text-red-600"
                      }
                    >
                      {book.available ? "Disponible" : "Vendu"}
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        book.available ? "text-green-600" : "text-red-600"
                      }
                    >
                      {book.available ? "Disponible" : "Vendu"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            <div className="px-5 pb-4">
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
                  {book.gradeLevel}
                </span>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
                  {book.subject}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
